import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  Grid,
  Container,
  Chip,
  Paper,
  Stack,
  Switch,
  Divider,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle,
  Star,
  Bolt,
  AutoAwesome,
  ArrowForward,
  CheckCircleOutline,
  ErrorOutline,
  Lock,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { fetchPricingPlans } from "../../Action/api";
import { getAuthTokenPayload } from "../../Utils/index";
import { API_URL as API_BASE } from "../../Config/index";

const annualDiscount = 0.2; // 20% off annual
const USD_TO_INR_RATE = 83; // Conversion rate: 1 USD = 83 INR

const planColors = {
  starter: {
    accent: "#6366f1",
    bg: "linear-gradient(135deg,#f0f0ff 0%,#ffffff 100%)",
    badge: "#6366f1",
  },
  professional: {
    accent: "#0ea5e9",
    bg: "linear-gradient(135deg,#e0f2fe 0%,#ffffff 100%)",
    badge: "#0ea5e9",
  },
  business_ai: {
    accent: "#8b5cf6",
    bg: "linear-gradient(135deg,#f5f3ff 0%,#ffffff 100%)",
    badge: "#8b5cf6",
  },
};

const planIcons = {
  starter: <Bolt sx={{ fontSize: 28 }} />,
  professional: <Star sx={{ fontSize: 28 }} />,
  business_ai: <AutoAwesome sx={{ fontSize: 28 }} />,
};

// ── Razorpay: load checkout script dynamically ──
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });


export function PricingPage() {
  const location = useLocation();
  // userId passed via navigation state (from SubscriptionModal) or fallback to JWT
  const userId = location.state?.userId || getAuthTokenPayload()?.userId || getAuthTokenPayload()?.staffId || null;

  const [annual, setAnnual] = useState(false);
  const [currency] = useState('INR'); // INR only
  const [paymentResult, setPaymentResult] = useState(null);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [payingPlanId, setPayingPlanId] = useState(null); // tracks which plan is processing

  useEffect(() => {
    const loadPlans = async () => {
      setPlansLoading(true);
      const result = await fetchPricingPlans("pricePlan");
      if (result.success) setPricingPlans(result.data);
      setPlansLoading(false);
    };
    loadPlans();
  }, []);

  const getPrice = (raw) => {
    const num = parseFloat(raw.replace(/[₹$]/g, ""));
    if (isNaN(num)) return raw;
    
    let priceInINR = annual ? (num * (1 - annualDiscount)) : num;
    return `₹${Math.round(priceInINR)}`;
  };

  // ── Razorpay payment handler ──
  const handlePayment = async (plan) => {
    setPayingPlanId(plan.id);
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      alert("Razorpay SDK failed to load. Please check your connection.");
      setPayingPlanId(null);
      return;
    }

    const rawPrice = plan.price.replace(/[₹$]/g, "");
    const baseAmount = parseFloat(rawPrice);
    let finalAmount = annual
      ? Math.round(baseAmount * (1 - annualDiscount))
      : baseAmount;
    
    const razorpayCurrency = 'INR';
    const razorpayAmount = finalAmount * 100; // paise for INR

    // ── Step 1: Create Razorpay order via backend ──
    let order;
    try {
      const orderRes = await fetch(`${API_BASE}/payment/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: razorpayAmount,
          currency: razorpayCurrency,
          receipt: `order_${plan.id}_${Date.now()}`,
          notes: `${plan.name} Plan — ${annual ? "Annual" : "Monthly"} — INR`,
          userId,
          planId: plan.id,
        }),
      });
      if (!orderRes.ok) throw new Error("Order creation failed");
      const rawOrder = await orderRes.json();
      console.log("Raw order response from backend:", rawOrder);

      // Backend shape: { status, message, data: { orderId, amount, currency, ... } }
      order = rawOrder?.data || rawOrder;

      if (!order?.id || !String(order.id).startsWith("order_")) {
        console.error("Invalid or missing Razorpay order id:", order?.id);
        setPaymentResult({ success: false, message: "Order ID from backend is invalid. Please check the backend response." });
        return;
      }
      console.log("Using order_id:", order.id);
    } catch (err) {
      console.error("create-order error:", err);
      alert("Could not create payment order. Please try again.");
      setPayingPlanId(null);
      return;
    }
    // ── Step 2: Open Razorpay checkout with order_id ──
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: order.amount,
      currency: order.currency,
      name: "OruClick",
      description: `${plan.name} Plan — ${annual ? "Annual" : "Monthly"}`,
      order_id: order.id, // ← ensures all 3 fields are returned in handler callback
      handler: async function (response) {
        setPayingPlanId(null);
        const { razorpay_order_id, razorpay_payment_id, razorpay_signature } = response;

        console.log("Payment response:", {
          razorpay_order_id,
          razorpay_payment_id,
          razorpay_signature,
        });

        // ── Step 3: Send all 3 fields to backend for signature verification ──
        try {
          const verifyRes = await fetch(`${API_BASE}/payment/verify-payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id,
              razorpay_payment_id,
              razorpay_signature,
              userId,
              planId: plan.id,
              billingCycle: annual ? "annual" : "monthly",
            }),
          });
          const result = await verifyRes.json();
          if (result.status !== false) {
            // If the backend returns a fresh token (with updated menuList), store it now
            // so the header re-reads the correct permissions after redirect.
            if (result.authorizationToken) {
              localStorage.setItem("authToken", result.authorizationToken);
            }
            setPaymentResult({
              success: true,
              paymentId: razorpay_payment_id,
              orderId: razorpay_order_id,
              planName: plan.name,
            });
          } else {
            setPaymentResult({
              success: false,
              message: result.message || "Signature mismatch. Please contact support.",
            });
          }
        } catch (err) {
          console.error("verify-payment error:", err);
          setPaymentResult({ success: false, message: "Payment done but verification failed. Contact support." });
        }
      },
      method: {
        emi: false,       // hide EMI option
        paylater: false,  // hide Pay Later option
      },
      theme: { color: "#6366f1" },
    };

    const rzp = new window.Razorpay(options);
    
    // Handle payment failure
    rzp.on("payment.failed", function (response) {
      setPayingPlanId(null);
      setPaymentResult({ success: false, message: response.error.description || "Payment failed. Please try again." });
      // Ensure body scroll is restored
      document.body.style.overflow = 'auto';
    });
    
    // Handle modal dismiss (user closes popup without paying)
    rzp.on("modal.ondismiss", function () {
      setPayingPlanId(null);
      // Restore body scroll when modal is dismissed
      document.body.style.overflow = 'auto';
    });
    
    rzp.open();
    // Dismiss loader once Razorpay modal opens
    setPayingPlanId(null);
  };

  return (
    <Box sx={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>

      {/* ── Payment Processing Overlay ── */}
      <Dialog
        open={!!payingPlanId}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 340,
            width: "100%",
            overflow: "hidden",
            boxShadow: "0 32px 64px rgba(0,0,0,0.25)",
            background: "linear-gradient(135deg,#0f172a 0%,#1e1b4b 100%)",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              py: 5,
              px: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2.5,
            }}
          >
            {/* Animated ring */}
            <Box sx={{ position: "relative", width: 80, height: 80 }}>
              <CircularProgress
                size={80}
                thickness={2.5}
                sx={{
                  color: "#818cf8",
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              />
              <CircularProgress
                size={80}
                thickness={2.5}
                variant="determinate"
                value={25}
                sx={{
                  color: "#6366f1",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  opacity: 0.3,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Lock sx={{ color: "#a5b4fc", fontSize: 28 }} />
              </Box>
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h6"
                sx={{ color: "#fff", fontWeight: 700, mb: 0.75 }}
              >
                Preparing Payment
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.55)", lineHeight: 1.6 }}
              >
                Connecting to secure payment gateway…
              </Typography>
            </Box>

            {/* Animated dots */}
            <Stack direction="row" spacing={0.75}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#6366f1",
                    animation: `dotPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                    "@keyframes dotPulse": {
                      "0%, 100%": { opacity: 0.25, transform: "scale(0.8)" },
                      "50%": { opacity: 1, transform: "scale(1.2)" },
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Payment Result Dialog ── */}
      <Dialog
        open={!!paymentResult}
        onClose={() => setPaymentResult(null)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 420,
            width: "100%",
            overflow: "hidden",
            boxShadow: "0 32px 64px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {paymentResult?.success ? (
            <Box>
              {/* Green top bar */}
              <Box
                sx={{
                  background: "linear-gradient(135deg,#10b981,#059669)",
                  py: 4,
                  textAlign: "center",
                }}
              >
                <CheckCircleOutline sx={{ fontSize: 72, color: "#fff", mb: 1 }} />
                <Typography variant="h5" fontWeight={700} color="#fff">
                  Payment Successful!
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                  Your subscription is now active
                </Typography>
              </Box>

              {/* Details */}
              <Box sx={{ px: 3.5, py: 3 }}>
                <Stack spacing={2}>
                  {[
                    { label: "Payment ID", value: paymentResult.paymentId },
                    { label: "Order ID",   value: paymentResult.orderId },
                  ].map(({ label, value }) => (
                    <Box
                      key={label}
                      sx={{
                        backgroundColor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: 2,
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#0f172a"
                        sx={{ wordBreak: "break-all", mt: 0.25 }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={() => {
                    // Hard reload so the header re-initialises with the updated token/menuList
                    window.location.href = "/dashboard";
                  }}
                  sx={{
                    mt: 3,
                    py: 1.4,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: 15,
                    background: "linear-gradient(90deg,#10b981,#059669)",
                    "&:hover": { background: "linear-gradient(90deg,#059669,#047857)" },
                  }}
                >
                  Go to Dashboard
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              {/* Red top bar */}
              <Box
                sx={{
                  background: "linear-gradient(135deg,#ef4444,#dc2626)",
                  py: 4,
                  textAlign: "center",
                }}
              >
                <ErrorOutline sx={{ fontSize: 72, color: "#fff", mb: 1 }} />
                <Typography variant="h5" fontWeight={700} color="#fff">
                  Payment Failed
                </Typography>
              </Box>

              <Box sx={{ px: 3.5, py: 3 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  lineHeight={1.7}
                  mb={3}
                >
                  {paymentResult?.message || "Something went wrong. Please try again."}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setPaymentResult(null)}
                  sx={{
                    py: 1.4,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: 15,
                    background: "linear-gradient(90deg,#ef4444,#dc2626)",
                    "&:hover": { background: "linear-gradient(90deg,#dc2626,#b91c1c)" },
                  }}
                >
                  Try Again
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      {/* ── Hero ── */}
      <Box
        sx={{
          background: "linear-gradient(160deg,#0f172a 0%,#1e1b4b 60%,#0f172a 100%)",
          py: { xs: 8, md: 12 },
          px: 2,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background glow blobs */}
        <Box
          sx={{
            position: "absolute",
            top: "20%",
            left: "10%",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "radial-gradient(circle,rgba(99,102,241,0.25) 0%,transparent 70%)",
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            right: "8%",
            width: 350,
            height: 350,
            borderRadius: "50%",
            background: "radial-gradient(circle,rgba(14,165,233,0.2) 0%,transparent 70%)",
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />

        <Chip
          label="Simple, transparent pricing"
          size="small"
          sx={{
            background: "rgba(99,102,241,0.18)",
            color: "#a5b4fc",
            border: "1px solid rgba(99,102,241,0.35)",
            fontWeight: 600,
            mb: 3,
          }}
        />

        <Typography
          variant="h2"
          fontWeight={800}
          sx={{
            color: "#fff",
            fontSize: { xs: "2rem", md: "3rem" },
            mb: 2,
            lineHeight: 1.2,
          }}
        >
          Choose the plan that{" "}
          <Box component="span" sx={{ color: "#818cf8" }}>
            works for you
          </Box>
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: "#94a3b8", maxWidth: 520, mx: "auto", mb: 4 }}
        >
          No hidden fees. Cancel anytime. Start free — upgrade when you're ready.
        </Typography>

        {/* Billing Cycle Toggle */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ mb: 2 }}>
            <Typography sx={{ color: annual ? "#64748b" : "#e2e8f0", fontWeight: 600 }}>
              Monthly
            </Typography>
            <Switch
              checked={annual}
              onChange={() => setAnnual((p) => !p)}
              sx={{
                "& .MuiSwitch-thumb": { backgroundColor: "#6366f1" },
                "& .MuiSwitch-track": { backgroundColor: "#4338ca" },
              }}
            />
            <Typography sx={{ color: annual ? "#e2e8f0" : "#64748b", fontWeight: 600 }}>
              Annual
            </Typography>
            {annual && (
              <Chip
                label="Save 20%"
                size="small"
                sx={{ backgroundColor: "#10b981", color: "#fff", fontWeight: 700 }}
              />
            )}
          </Stack>
      </Box>

      {/* ── Plans ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 }, position: "relative", mt: -4 }}>
        {plansLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#6366f1' }} size={48} />
          </Box>
        ) : (
        <Grid container spacing={3} alignItems="stretch">
          {pricingPlans.map((plan) => {
            const colors = planColors[plan.id] || planColors.starter;
            const isPopular = plan.popular;

            return (
              <Grid item xs={12} md={4} key={plan.id}>
                <Paper
                  elevation={isPopular ? 16 : 3}
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: isPopular
                      ? `2px solid ${colors.accent}`
                      : "2px solid transparent",
                    position: "relative",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: `0 24px 48px rgba(0,0,0,0.14)`,
                    },
                  }}
                >
                  {/* Popular badge */}
                  {isPopular && (
                    <Box
                      sx={{
                        background: colors.accent,
                        color: "#fff",
                        textAlign: "center",
                        py: 0.75,
                        fontWeight: 700,
                        fontSize: 13,
                        letterSpacing: 1,
                      }}
                    >
                      ✦ MOST POPULAR
                    </Box>
                  )}

                  {/* Card header */}
                  <Box sx={{ background: colors.bg, px: 3.5, pt: 3.5, pb: 2.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: 2.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: colors.accent,
                          color: "#fff",
                          flexShrink: 0,
                        }}
                      >
                        {planIcons[plan.id]}
                      </Box>
                      <Box>
                        <Typography fontWeight={700} fontSize={18} color="#0f172a">
                          {plan.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {plan.description}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Price */}
                    <Stack direction="row" alignItems="flex-end" spacing={0.5} mt={2}>
                      <Typography
                        fontWeight={800}
                        fontSize={48}
                        lineHeight={1}
                        color="#0f172a"
                      >
                        {getPrice(plan.price)}
                      </Typography>
                      <Typography color="text.secondary" mb={0.8}>
                        /mo
                      </Typography>
                    </Stack>
                    {annual && (
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
                        Billed annually &nbsp;•&nbsp;{" "}
                        <Box component="span" sx={{ textDecoration: "line-through" }}>
                          {plan.price}/mo
                        </Box>
                      </Typography>
                    )}
                  </Box>

                  <Divider />

                  {/* Features */}
                  <Box sx={{ px: 3.5, py: 3, flex: 1 }}>
                    <Stack spacing={1.4}>
                      {plan.features.map((feat, i) => (
                        <Stack key={i} direction="row" alignItems="flex-start" spacing={1}>
                          <CheckCircle
                            sx={{ fontSize: 18, color: colors.accent, mt: 0.15, flexShrink: 0 }}
                          />
                          <Typography variant="body2" color="#374151" lineHeight={1.5}>
                            {feat}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>

                  {/* CTA */}
                  <Box sx={{ px: 3.5, pb: 3.5 }}>
                    <Button
                      fullWidth
                      variant={isPopular ? "contained" : "outlined"}
                      disabled={!!payingPlanId}
                      endIcon={payingPlanId === plan.id ? null : <ArrowForward />}
                      onClick={() => handlePayment(plan)}
                    sx={{
                        borderRadius: 2.5,
                        py: 1.4,
                        fontWeight: 700,
                        fontSize: 15,
                        textTransform: "none",
                        ...(isPopular
                          ? {
                              background: `linear-gradient(90deg,${colors.accent},#818cf8)`,
                              boxShadow: `0 6px 20px ${colors.accent}55`,
                              "&:hover": {
                                background: `linear-gradient(90deg,#4f46e5,${colors.accent})`,
                              },
                            }
                          : {
                              borderColor: colors.accent,
                              color: colors.accent,
                              "&:hover": {
                                backgroundColor: `${colors.accent}10`,
                                borderColor: colors.accent,
                              },
                            }),
                      }}
                    >
                      {payingPlanId === plan.id ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CircularProgress size={16} thickness={3} sx={{ color: isPopular ? '#fff' : colors.accent }} />
                          <span>Processing…</span>
                        </Stack>
                      ) : (
                        "Proceed to Pay"
                      )}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        )}

        {/* Trust strip */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="center"
          alignItems="center"
          spacing={{ xs: 1.5, sm: 4 }}
          mt={7}
          flexWrap="wrap"
        >
          {[
            "No credit card required",
            "Cancel anytime",
            "SOC 2 compliant",
          ].map((t) => (
            <Stack key={t} direction="row" alignItems="center" spacing={0.75}>
              <CheckCircle sx={{ fontSize: 16, color: "#10b981" }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {t}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Container>

      {/* ── FAQ strip ── */}
      <Box sx={{ backgroundColor: "#fff", py: { xs: 6, md: 8 }, borderTop: "1px solid #e2e8f0" }}>
        <Container maxWidth="md">
          <Typography
            variant="h5"
            fontWeight={700}
            textAlign="center"
            mb={4}
            color="#0f172a"
          >
            Frequently asked questions
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                q: "Can I switch plans later?",
                a: "Yes — upgrade or downgrade at any time from your account settings. Changes take effect immediately.",
              },
              {
                q: "Is there a team discount?",
                a: "The Professional plan includes up to 3 users. For larger teams, contact us for custom enterprise pricing.",
              },
            ].map((item) => (
              <Grid item xs={12} sm={6} key={item.q}>
                <Box>
                  <Typography fontWeight={600} color="#0f172a" mb={0.5}>
                    {item.q}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                    {item.a}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default PricingPage;
